import HiddenInput from "@/components/form/components/HiddenInput";
import { resolveFormValidationError } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, InputRef, Modal } from "antd";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCreateDocumentNode, useUpdateDocumentNode } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import { DocumentNodeType } from "../../enums";
import { CreateUpdateDocumentNode, DocumentNodeTree } from "../../types";

interface Props {
  isOpen: boolean;
  type: "createFolder" | "renameNode";
  nodeToRename?: DocumentNodeTree;
  onFormCancel: () => void;
  currentNode?: DocumentNodeTree;
}

const DocumentNodeNameModalForm = ({ isOpen, type, nodeToRename, onFormCancel, currentNode }: Props) => {
  const { t } = useTranslation();

  const inputRef = useRef<InputRef>(null);
  const [form] = Form.useForm<CreateUpdateDocumentNode>();

  const createDocumentNode = useCreateDocumentNode();
  const updateDocumentNode = useUpdateDocumentNode();

  useEffect(() => {
    if (inputRef.current && isOpen) {
      inputRef.current.focus({
        cursor: "end",
      });
    }
  }, [inputRef, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (nodeToRename) {
      form.setFieldsValue({
        name: nodeToRename?.name,
        optimisticLockVersion: nodeToRename?.optimisticLockVersion,
        type: nodeToRename?.type,
        parentId: currentNode?.id,
      });
    } else {
      form.setFieldsValue({
        name: "",
        type: currentNode?.type ?? DocumentNodeType.GENERAL,
        parentId: currentNode?.id,
      });
    }
  }, [isOpen, nodeToRename]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (type === "createFolder") {
          createDocumentNode.mutate(values, {
            onSuccess: onFormCancel,
          });
        } else if (nodeToRename) {
          updateDocumentNode.mutate(
            {
              id: nodeToRename.id,
              object: values,
            },
            {
              onSuccess: onFormCancel,
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={isOpen}
      title={t(`documentNode.titles.${type}`)}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createDocumentNode.isPending || updateDocumentNode.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="documentNodeNameForm">
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name="type" />
        <HiddenInput name="parentId" />

        <Form.Item
          name="name"
          label={t("documentNode.attrs.name")}
          rules={[validations.notBlank, validations.size(1, 255)]}
        >
          <Input ref={inputRef} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DocumentNodeNameModalForm;
